import { Component, computed, effect, inject } from '@angular/core';
import { createMeasureColumn, DataRow } from '../../shared/dashboard/data-tree-table/data-tree-table';
import {
	AanwezigheidMeasure,
	BasicFilterExpression,
	CompoundFilterExpression,
	DataResponse,
	DataService,
	ExportDataOptions,
	ExportFilter,
	FilterExpression,
} from '../../services/data.service';
import { Observable } from 'rxjs';
import { maxOverAtLevel, MultiAggregator, noAgg0, noAggFiltered0, xAgg0 } from '../../services/aggregation';
import { getLeafA, Level, Path } from '../../services/data-tree';
import { att, percOfRow } from '../../services/measures';
import { ColumnDef } from '../../shared/components/table/table/table.model';
import { fromPairs, intersection, isNil, last, max, nth, orderBy, range } from 'lodash-es';
import { BarInfo } from '../../services/stacked-bars';
import { PointInfo } from '../../shared/dashboard/linechart-table/linechart/linechart.component';
import { deelVeilig, formatDurationUrenMinuten, generateCssClassForString } from '@cumlaude/shared-utils';
import { formatNumber, formatPercent } from '@angular/common';
import { getAbsentieKlasse } from '../../core/services/enum.service';
import { BarchartTableConfig } from '../../shared/dashboard/barchart-table/barchart-table-config';
import { LinechartTableConfig } from '../../shared/dashboard/linechart-table/linechart-table-config';
import { Attributes, SelectionConfig } from '../../shared/dashboard/base-dashboard/base-dashboard-config';
import { DashboardContext, DataRequestContext } from '../../shared/dashboard/base-dashboard/dashboard-context';
import { AttrPath, Table } from '@cumlaude/metadata';
import { AbsentieKlasseHistorie } from '../../services/label-enums';
import { Axis, createYAxis } from '../../services/axis';
import { DashboardVariant, Interval, Tijdseenheid } from '../../services/weergave-opties';
import { ExportType } from '../../services/export.service';
import { formatDecimal } from '@cumlaude/shared-pipes';
import { LinechartTableComponent } from '../../shared/dashboard/linechart-table/linechart-table.component';
import { BarchartTableComponent } from '../../shared/dashboard/barchart-table/barchart-table.component';
import { DashboardHeaderComponent } from '../../dashboard-header/dashboard-header.component';
import { FilterPanelComponent } from '../../filter-panel/filter-panel.component';
import { DashboardContainerComponent } from '../../layout/dashboard-container/dashboard-container.component';
import { WeergaveOptieComponent } from '../../shared/components/weergave-optie/weergave-optie.component';
import { FilterBarComponent } from '../../filter-bar/filter-bar.component';
import { FilterName } from '../../services/filter-name';
import { LegendaComponent } from '../../legenda/legenda.component';
import { ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';

interface AfwezigheidI extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_filtered: number;
	aw_m_nr_lesdagen: number;
	aw_m_nr_lesdagen_filtered: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_afwezig: number;
	aw_m_nr_minuten_filtered: number;
	aw_m_nr_leerlingen: number;
	aw_is_abs_geoorloofd: '0' | '1' | null;
	xa: { [nr: number]: { aw_nr_lesuren: number } };
}

interface AfwezigheidA extends Attributes {
	aw_m_nr_lesuren: number;
	aw_m_nr_lesuren_x: number;
	aw_m_nr_lesuren_filtered: number;
	max_uren_filtered: number;
	aw_m_nr_lesdagen: number;
	aw_m_nr_lesdagen_x: number;
	aw_m_nr_lesdagen_filtered: number;
	max_dagen_filtered: number;
	aw_m_nr_minuten: number;
	aw_m_nr_minuten_afwezig: number;
	aw_m_nr_minuten_x: number;
	aw_m_nr_minuten_filtered: number;
	max_minuten_filtered: number;
	aw_m_nr_leerlingen_filtered: number;
}

@Component({
	selector: 'app-afwezigheid',
	templateUrl: './afwezigheid.component.html',
	styleUrls: ['./afwezigheid.component.scss'],
	imports: [
		DashboardContainerComponent,
		FilterPanelComponent,
		DashboardHeaderComponent,
		BarchartTableComponent,
		LinechartTableComponent,
		WeergaveOptieComponent,
		FilterBarComponent,
		LegendaComponent,
		ToggleButtonsComponent,
	],
})
export class AfwezigheidComponent extends BarchartTableConfig<AfwezigheidI, AfwezigheidA> {
	private readonly dataService = inject(DataService);

	override availableGroups: AttrPath[] = [
		['aw_fk_ilt', 'ilt_nm_niveau'],
		['aw_nr_leerjaar'],
		['aw_fk_ilt', 'ilt_nm_opleiding'],
		['aw_nm_klas'],
		['aw_fk_ilt', 'ilt_abb_profiel'],
		['aw_fun_nm_abs_reden'],
		['aw_fk_vs', 'vs_nm_vestiging'],
		['aw_fk_ll', 'll_nm_leerling'],
		['aw_fks_mw', 'mw_nm_medewerker'],
		['aw_fun_nm_vak'],
	];

	fixedSubgroups: AttrPath[] = [['aw_is_abs_geoorloofd']];

	permanentFilterExpressions = [];

	readonly historieGroups = computed(() => this.groeperingService.huidigeGroeperingen().slice(0, -1));

	readonly historieSubgroups = computed(() => [
		...this.groeperingService.huidigeGroeperingen().slice(-1),
		...this.intervalSubgroups(),
		...this.fixedSubgroups,
	]);

	readonly intervalSubgroups = computed<AttrPath[]>(() => {
		switch (this.interval()) {
			case Interval.MAAND:
				return [
					['aw_d_datum', 'per_nm_schooljaar'],
					['aw_d_datum', 'per_nr_maand'],
				];
			case Interval.SCHOOLJAAR:
				return [['aw_d_datum', 'per_nm_schooljaar']];
			case Interval.WEEK:
				return [
					['aw_d_datum', 'per_nm_schooljaar'],
					['aw_d_datum', 'per_nr_isoweek'],
				];
		}
	});

	readonly nrIntervalSubgroups = computed(() => <1 | 2>this.intervalSubgroups().length);

	actueelFilters: FilterName[] = [
		'aw_nm_schooljaar',
		'aw_fk_lb.lb_co_brin',
		'aw_fk_vs.vs_nm_vestiging',
		'aw_fk_ilt.ilt_nm_niveau',
		'aw_nr_leerjaar',
		'aw_nm_klas',
		'aw_is_abs_geoorloofd',
		'aw_fun_nm_abs_reden',
		'aw_fun_nm_vak',
		'aw_d_datum.per_d_datum',
	];

	historieFilters: FilterName[] = [
		'aw_nm_schooljaar', // is onzichtbaar en filtert niet, maar is alleen om het schooljaar van de actueel-variant vast te houden
		'x_aw_schooljaar_historie',
		'x_aw_multiselect_schooljaar',
		'aw_fk_vs.vs_nm_vestiging',
		'aw_fk_ilt.ilt_nm_niveau',
		'aw_nr_leerjaar',
		'aw_nm_klas',
		'x_abs_geoorloofd',
		'aw_fun_nm_abs_reden',
		'aw_fun_nm_vak',
		'aw_d_datum.per_d_datum',
	];

	variant = this.qp.signal('variant');

	tijdseenheid = this.qp.signal('tijdseenheid');

	interval = this.qp.signal('interval');

	visibleLines: AbsentieKlasseHistorie[] = Object.values(AbsentieKlasseHistorie);

	protected actueelExportTypes = [ExportType.AFBEELDING, ExportType.PDF, ExportType.TABEL, ExportType.DATA];
	protected historieExportTypes = [ExportType.AFBEELDING, ExportType.PDF, ExportType.DATA];

	protected override singleAggregators = {
		aw_m_nr_lesuren: noAgg0<'aw_m_nr_lesuren', AfwezigheidI>('aw_m_nr_lesuren'),
		aw_m_nr_lesuren_filtered: noAggFiltered0('aw_m_nr_lesuren'),
		aw_m_nr_lesdagen: noAgg0<'aw_m_nr_lesdagen', AfwezigheidI>('aw_m_nr_lesdagen'),
		aw_m_nr_lesdagen_filtered: noAggFiltered0('aw_m_nr_lesdagen'),
		aw_m_nr_minuten: noAgg0<'aw_m_nr_minuten', AfwezigheidI>('aw_m_nr_minuten'),
		aw_m_nr_minuten_filtered: noAggFiltered0('aw_m_nr_minuten_afwezig'),
		aw_m_nr_leerlingen: noAgg0<'aw_m_nr_leerlingen', AfwezigheidI>('aw_m_nr_leerlingen'),
		aw_m_nr_leerlingen_filtered: noAggFiltered0('aw_m_nr_leerlingen'),
		aw_m_nr_lesuren_x: xAgg0<'aw_m_nr_lesuren', AfwezigheidI>('aw_m_nr_lesuren'),
		aw_m_nr_lesdagen_x: xAgg0<'aw_m_nr_lesdagen', AfwezigheidI>('aw_m_nr_lesdagen'),
		aw_m_nr_minuten_x: xAgg0<'aw_m_nr_minuten', AfwezigheidI>('aw_m_nr_minuten'),
	};

	protected override multiAggregators: MultiAggregator<keyof AfwezigheidA, AfwezigheidI, AfwezigheidA, number>[] = [
		maxOverAtLevel('max_uren_filtered', 'aw_m_nr_lesuren_filtered', () => this.groeperingService.huidigeAantalGroeperingen()),
		maxOverAtLevel('max_dagen_filtered', 'aw_m_nr_lesdagen_filtered', () => this.groeperingService.huidigeAantalGroeperingen()),
		maxOverAtLevel('max_minuten_filtered', 'aw_m_nr_minuten_filtered', () => this.groeperingService.huidigeAantalGroeperingen()),
	];

	linechartConfig = new (class extends LinechartTableConfig<AfwezigheidI, AfwezigheidA> {
		constructor(private readonly dashboard: AfwezigheidComponent) {
			super();
		}

		override makePoints(r: Path<AfwezigheidA, number[]>[]): PointInfo[] {
			const totalClass = generateCssClassForString(AbsentieKlasseHistorie.Totaal);
			return [...this.dashboard.getVisibleQties(r)].map(([lineClass, qty]) => ({
				lineClass: lineClass == totalClass ? 'default-fill' : lineClass,
				qty,
			}));
		}

		/**
		 * maximum van de zichtbare lijnen (in relevante eenheid) van alle grafieken van de batch.
		 */
		override getYAxis(path: Path<AfwezigheidA, number[]>): Axis {
			const batchMax = max(nth(path, -2)!.xr!.map((rec) => max([...this.dashboard.getVisibleQties([rec]).values()]))) || 100;

			switch (this.dashboard.tijdseenheid()) {
				case Tijdseenheid.UREN_PERCENTAGE:
				case Tijdseenheid.TIJD_PERCENTAGE:
					return createYAxis([0, batchMax], 5, (val) => formatPercent(val, 'nl_NL'));
				case Tijdseenheid.TIJD:
					return createYAxis([0, batchMax], 5, (val) => formatDurationUrenMinuten(val, 'h'));
				default:
					return createYAxis([0, batchMax], 5, (val) => formatDecimal(val, '1.0-1'));
			}
		}

		override getLineNames(): { [lineClass: string]: string } {
			return {
				...fromPairs(Object.values(AbsentieKlasseHistorie).map((cls) => [generateCssClassForString(cls), cls])),
				'default-fill': 'Totaal',
			};
		}

		override getQtyFormatter() {
			switch (this.dashboard.tijdseenheid()) {
				case Tijdseenheid.UREN_PERCENTAGE:
				case Tijdseenheid.TIJD_PERCENTAGE:
					return (val: number) => formatPercent(val, 'nl_NL', '1.2-2');
				case Tijdseenheid.TIJD:
					return (val: number) => formatDurationUrenMinuten(val, 'u m');
				default:
					return undefined;
			}
		}

		factTable = Table.fac_aw_aanwezigheid;
		getData = this.dashboard.getData.bind(this.dashboard);
		override getExportData = this.dashboard.getExportData.bind(this.dashboard);
		override isHistorieBatchVariant = this.dashboard.isHistorieBatchVariant.bind(this.dashboard);
		override getSelectionConfig = this.dashboard.getSelectionConfig.bind(this.dashboard);
		override dataRootSelection = this.dashboard.dataRootSelection;
		override availableGroups = this.dashboard.availableGroups;
		override singleAggregators = this.dashboard.singleAggregators;
		override multiAggregators = this.dashboard.multiAggregators;
		override enrichTableModel = this.dashboard.enrichTableModel.bind(this.dashboard);
	})(this);

	constructor() {
		super();

		this.subscriptions.push(
			this.filterService.observe('x_abs_geoorloofd').subscribe((visibleLines) => {
				this.visibleLines = visibleLines?.length ? visibleLines : Object.values(AbsentieKlasseHistorie);
			})
		);
		effect(() => {
			this.tijdseenheid();
			this.filterService.refresh();
		});
	}

	/**
	 * De getoonde waarden voor bar/line zijn afhankelijk van de gekozen Tijdseenheid.
	 */
	getRelevantQty(path: Path<AfwezigheidA, number[]>): number {
		return this.getQty(path, this.tijdseenheid());
	}

	getQty(path: Path<AfwezigheidA, number[]>, tijdseenheid: Tijdseenheid): number {
		const attrs = last(path)!.a;
		switch (tijdseenheid) {
			case Tijdseenheid.UREN:
				return attrs.aw_m_nr_lesuren;
			case Tijdseenheid.UREN_PERCENTAGE:
				return deelVeilig(attrs.aw_m_nr_lesuren, attrs.aw_m_nr_lesuren_x);
			case Tijdseenheid.DAGEN:
				return attrs.aw_m_nr_lesdagen;
			case Tijdseenheid.TIJD:
				return attrs.aw_m_nr_minuten_filtered;
			case Tijdseenheid.TIJD_PERCENTAGE:
				return deelVeilig(attrs.aw_m_nr_minuten_filtered, attrs.aw_m_nr_minuten_x);
		}
	}

	/**
	 * De totaal-lijn representeert de totale ABSENTIE en niet het totaal aantal uren/dagen.
	 */
	getRelevantTotal(path: Path<AfwezigheidA, number[]>): number {
		const attrs = nth(path, -2)!.a;
		switch (this.tijdseenheid()) {
			case Tijdseenheid.UREN:
				return attrs.aw_m_nr_lesuren_filtered;
			case Tijdseenheid.UREN_PERCENTAGE:
				return deelVeilig(attrs.aw_m_nr_lesuren_filtered, attrs.aw_m_nr_lesuren_x);
			case Tijdseenheid.DAGEN:
				return attrs.aw_m_nr_lesdagen_filtered;
			case Tijdseenheid.TIJD:
				return attrs.aw_m_nr_minuten_filtered;
			case Tijdseenheid.TIJD_PERCENTAGE:
				return deelVeilig(attrs.aw_m_nr_minuten_filtered, attrs.aw_m_nr_minuten_x);
		}
	}

	/**
	 * Genereer de waardes in de relevante eenheid voor de zichtbare lijnen (volgens het x_abs_geoorloofd "filter").
	 * Als er geen record is voor een bepaalde lijn wordt de waarde 0.
	 */
	getVisibleQties(recs: Path<AfwezigheidA, number[]>[]): Map<string, number> {
		const ret = new Map<string, number>();
		// onderstaande volgorde zorgt ervoor dat totaallijn over de andere getekend wordt
		intersection(
			[AbsentieKlasseHistorie.Ongeoorloofd, AbsentieKlasseHistorie.Geoorloofd, AbsentieKlasseHistorie.Totaal],
			this.visibleLines
		).forEach((clz) => ret.set(generateCssClassForString(clz), 0));

		for (const rec of recs) {
			const aw_is_abs_geoorloofd = last(rec)!.k;
			if (isNil(aw_is_abs_geoorloofd)) continue;

			const clz = generateCssClassForString(getAbsentieKlasse(aw_is_abs_geoorloofd));
			if (ret.get(clz) !== undefined) ret.set(clz, this.getRelevantQty(rec));
		}
		const totalClass = generateCssClassForString(AbsentieKlasseHistorie.Totaal);
		if (ret.get(totalClass) !== undefined && recs.length > 0) ret.set(totalClass, this.getRelevantTotal(recs[0]));
		return ret;
	}

	override makeBar(
		attrs: AfwezigheidI,
		path: Path<AfwezigheidA, number[]>,
		context: DashboardContext<AfwezigheidI, AfwezigheidA, AfwezigheidComponent>
	): BarInfo {
		if (isNil(attrs.aw_is_abs_geoorloofd)) {
			return {
				size: 0,
			};
		}

		const barInfo = super.makeBar(attrs, path, context);
		const size = this.getRelevantQty(path);
		const kls = getAbsentieKlasse(attrs.aw_is_abs_geoorloofd);
		return {
			...barInfo,
			size,
			description: kls,
			className: generateCssClassForString(kls),
			tooltip: [
				{
					label: 'Absentie',
					value: kls,
				},
				...this.getQtyTooltip(path),
			],
		};
	}

	getQtyTooltip(path: Path<AfwezigheidA, number[]>) {
		switch (this.tijdseenheid()) {
			case Tijdseenheid.UREN_PERCENTAGE:
				return [
					{ label: Tijdseenheid.UREN_PERCENTAGE, value: formatPercent(this.getQty(path, Tijdseenheid.UREN_PERCENTAGE), 'nl', '1.1-1') },
				];
			case Tijdseenheid.DAGEN:
				return [{ label: Tijdseenheid.DAGEN, value: formatNumber(this.getQty(path, Tijdseenheid.DAGEN), 'nl-NL') }];
			default:
				return [
					{ label: Tijdseenheid.UREN, value: formatNumber(this.getQty(path, Tijdseenheid.UREN), 'nl-NL') },
					{ label: Tijdseenheid.TIJD, value: formatDurationUrenMinuten(this.getQty(path, Tijdseenheid.TIJD), 'u m') },
				];
		}
	}

	/**
	 * Eerst geoorloofd, daarna ongeoorloofd.
	 */
	override partitionBarData(rowRoot: Level<AfwezigheidA, number[]>): Path<AfwezigheidA, number[]>[][] {
		return [orderBy(rowRoot.r, [(path) => last(path)!.k], ['desc'])];
	}

	override createMeasureColumns(context: DashboardContext<AfwezigheidI, AfwezigheidA, AfwezigheidComponent>): ColumnDef<DataRow<AfwezigheidA>>[] {
		switch (this.tijdseenheid()) {
			case Tijdseenheid.UREN:
				return [
					createMeasureColumn('Lesuren', att('aw_m_nr_lesuren_filtered')),
					this.createLeerlingColumn('Leerlingen', att('aw_m_nr_leerlingen_filtered'), { context }, att('aw_m_nr_leerlingen_filtered')),
					createMeasureColumn('Totaal lesuren', getTotaalLesuren),
				];
			case Tijdseenheid.DAGEN:
				return [
					createMeasureColumn('Lesdagen', att('aw_m_nr_lesdagen_filtered')),
					this.createLeerlingColumn('Leerlingen', att('aw_m_nr_leerlingen_filtered'), { context }, att('aw_m_nr_leerlingen_filtered')),
					createMeasureColumn('Totaal lesdagen', getTotaalLesdagen),
				];
			case Tijdseenheid.UREN_PERCENTAGE:
				return [
					createMeasureColumn('Lesuren (%)', getPercentageLesuren, {
						dataType: 'percentage',
						format: '1.2-2',
					}),
					createMeasureColumn('Lesuren', att('aw_m_nr_lesuren_filtered')),
					this.createLeerlingColumn('Leerlingen', att('aw_m_nr_leerlingen_filtered'), { context }, att('aw_m_nr_leerlingen_filtered')),
					createMeasureColumn('Totaal lesuren', getTotaalLesuren),
				];
			case Tijdseenheid.TIJD:
				return [
					createMeasureColumn('Lestijd', att('aw_m_nr_minuten_filtered'), { dataType: 'minutes', format: 'uur' }),
					this.createLeerlingColumn('Leerlingen', att('aw_m_nr_leerlingen_filtered'), { context }, att('aw_m_nr_leerlingen_filtered')),
					createMeasureColumn('Totaal lestijd', getTotaalMinuten, { dataType: 'minutes', format: 'uur' }),
				];
			case Tijdseenheid.TIJD_PERCENTAGE:
				return [
					createMeasureColumn('Lestijd (%)', getPercentageMinuten, {
						dataType: 'percentage',
						format: '1.2-2',
					}),
					createMeasureColumn('Lestijd', att('aw_m_nr_minuten_filtered'), { dataType: 'minutes', format: 'uur' }),
					this.createLeerlingColumn('Leerlingen', att('aw_m_nr_leerlingen_filtered'), { context }, att('aw_m_nr_leerlingen_filtered')),
					createMeasureColumn('Totaal lestijd', getTotaalMinuten, { dataType: 'minutes', format: 'uur' }),
				];
		}
	}

	override isHistorieBatchVariant(): boolean {
		return this.variant() === DashboardVariant.HISTORIE && this.groeperingService.huidigeAantalGroeperingen() > 0;
	}

	override createXAxis(context: DashboardContext<AfwezigheidI, AfwezigheidA, AfwezigheidComponent>): Axis {
		const rootA = context.dataRoot!.a;
		switch (this.tijdseenheid()) {
			case Tijdseenheid.DAGEN:
				return { min: 0, max: rootA.max_dagen_filtered, ticks: [] };
			case Tijdseenheid.UREN:
				return { min: 0, max: rootA.max_uren_filtered, ticks: [] };
			case Tijdseenheid.UREN_PERCENTAGE: {
				const percFunc = percOfRow('aw_m_nr_lesuren_filtered', 'aw_m_nr_lesuren_x');
				const maxPerc = max(context.dataRoot!.r.map(percFunc)) || 0;
				return { min: 0, max: maxPerc, ticks: [] };
			}
			case Tijdseenheid.TIJD:
				return { min: 0, max: rootA.max_minuten_filtered, ticks: [] };
			case Tijdseenheid.TIJD_PERCENTAGE: {
				const percFunc = percOfRow('aw_m_nr_minuten_filtered', 'aw_m_nr_minuten_x');
				const maxPerc = max(context.dataRoot!.r.map(percFunc)) || 0;
				return { min: 0, max: maxPerc, ticks: [] };
			}
		}
	}

	override getBarchartQty(path: Path<AfwezigheidA, number[]>) {
		switch (this.tijdseenheid()) {
			case Tijdseenheid.DAGEN:
				return att<'aw_m_nr_lesdagen_filtered', AfwezigheidA>('aw_m_nr_lesdagen_filtered')(path);
			case Tijdseenheid.UREN:
				return att<'aw_m_nr_lesuren_filtered', AfwezigheidA>('aw_m_nr_lesuren_filtered')(path);
			case Tijdseenheid.UREN_PERCENTAGE:
				return percOfRow('aw_m_nr_lesuren_filtered', 'aw_m_nr_lesuren_x')(path);
			case Tijdseenheid.TIJD:
				return att<'aw_m_nr_minuten_filtered', AfwezigheidA>('aw_m_nr_minuten_filtered')(path);
			case Tijdseenheid.TIJD_PERCENTAGE:
				return percOfRow('aw_m_nr_minuten_filtered', 'aw_m_nr_minuten_x')(path);
		}
	}

	override getSelectionConfig(context: DataRequestContext<AfwezigheidA>): SelectionConfig<AfwezigheidA> | undefined {
		const extraFilters = [new BasicFilterExpression(['aw_is_absent'], 1)];
		const detailUrl = '/details/leerling/afwezigheid';
		const getSize = att<'aw_m_nr_leerlingen_filtered', AfwezigheidA>('aw_m_nr_leerlingen_filtered');
		return { ...super.getSelectionConfig(context)!, detailUrl, getSize, extraFilters };
	}

	factTable = Table.fac_aw_aanwezigheid;

	getData(options: { g: AttrPath[]; f?: FilterExpression }): Observable<DataResponse<number[]>> {
		const totaalLevel = this.getTotaalLevel();
		const xaggPaths = ['aw_is_abs_geoorloofd', 'aw_fun_nm_abs_reden'];
		const xa = [options.g.flatMap((path, ix) => (xaggPaths.includes(path.join('.')) ? [ix] : []))];

		const eenheidMeasures =
			this.tijdseenheid() === Tijdseenheid.DAGEN ? [AanwezigheidMeasure.LESDAGEN] : [AanwezigheidMeasure.LESUREN, AanwezigheidMeasure.LESTIJD];
		const m = [...eenheidMeasures, AanwezigheidMeasure.LEERLINGEN];

		// Verplaats aw_is_abs_geoorloofd en aw_fun_nm_abs_reden naar "having" zodat we de totalen berekenen hierop te filteren.
		const { f, having } = this.dataService.moveToHaving([['aw_is_abs_geoorloofd'], ['aw_fun_nm_abs_reden']], options);
		const dataOptions = {
			...options,
			r: range(0, totaalLevel + 1),
			m,
			f,
			having: having ?? new BasicFilterExpression(['aw_is_abs_geoorloofd'], null, '<>'),
			xa,
		};
		return this.dataService.getAanwezigheidData(dataOptions);
	}

	getTotaalLevel() {
		const isActueel = this.variant() === DashboardVariant.ACTUEEL;
		return (
			this.groeperingService.huidigeAantalGroeperingen() + (isActueel ? 0 : this.intervalSubgroups().length) + (this.fixedSubgroups.length - 1)
		);
	}

	override getExportData(options: ExportDataOptions) {
		return this.dataService.getAanwezigheidExportData({
			...options,
			f: new CompoundFilterExpression([options.f, new BasicFilterExpression(['aw_is_absent'], 1)]),
		});
	}

	protected readonly Tijdseenheid = Tijdseenheid;
	protected readonly Interval = Interval;
	protected readonly DashboardVariant = DashboardVariant;

	override getDisplayOptions(): ExportFilter[] {
		return this.variant() === DashboardVariant.HISTORIE ? [{ label: 'Eenheid', value: this.tijdseenheid() }] : [];
	}
}

function getTotaalLesuren(row: Path<AfwezigheidA, unknown>) {
	return row.length == 1 ? getLeafA(row).aw_m_nr_lesuren : getLeafA(row).aw_m_nr_lesuren_x;
}

function getTotaalLesdagen(row: Path<AfwezigheidA, unknown>) {
	return row.length == 1 ? getLeafA(row).aw_m_nr_lesdagen : getLeafA(row).aw_m_nr_lesdagen_x;
}

function getTotaalMinuten(row: Path<AfwezigheidA, unknown>) {
	return row.length == 1 ? getLeafA(row).aw_m_nr_minuten : getLeafA(row).aw_m_nr_minuten_x;
}

function getPercentageLesuren(row: Path<AfwezigheidA, unknown>) {
	const rowLesuren = getLeafA(row).aw_m_nr_lesuren_filtered;
	const totaal = getTotaalLesuren(row);
	return deelVeilig(rowLesuren, totaal);
}

function getPercentageMinuten(row: Path<AfwezigheidA, unknown>) {
	const rowMinuten = getLeafA(row).aw_m_nr_minuten_filtered;
	const totaal = getTotaalMinuten(row);
	return deelVeilig(rowMinuten, totaal);
}
